import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { trpc } from "~/api/trpc";
import { getCartItemGtmTotalPrice, toGtmCartItems, useBookingFlowStore } from "~/stores/booking-flow-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import toFixedNumber from "../../helper/common/toFixedNumber";
import { useShallow } from "zustand/react/shallow";
import { consumeEmbedReturnPage, handOverToDrawer } from "~/lib/embed";

type KlarnaReturnState = 'processing' | 'success' | 'error';

const KlarnaReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = useLanguageStore();
    const [state, setState] = useState<KlarnaReturnState>('processing');
    const [errorMessage, setErrorMessage] = useState('');
    const confirmedRef = useRef(false);
    const embedReturnPageRef = useRef<string | null>(null);
    const { sendEvent } = useGTM();

    const { selectedStore, selectedTimeslot, cartItems, email, phone, discountCode, resetBookingFlow } = useBookingFlowStore(
        useShallow((s) => ({
            selectedStore: s.selectedStore,
            selectedTimeslot: s.selectedTimeslot,
            cartItems: s.cartItems,
            email: s.email,
            phone: s.phone,
            discountCode: s.discountCode,
            resetBookingFlow: s.resetBookingFlow,
        }))
    );

    const confirmMutation = trpc.publicPayments.confirmKlarnaBooking.useMutation();
    const cancelCheckoutMutation = trpc.publicPayments.cancelKlarnaCheckout.useMutation();

    useEffect(() => {
        if (confirmedRef.current) return;
        confirmedRef.current = true;

        // Payment was started in the website drawer. The booking is still confirmed here; the drawer only shows the result.
        const embedReturnPage = consumeEmbedReturnPage();
        embedReturnPageRef.current = embedReturnPage;

        const klarnaAuthId = searchParams.get('klarna_auth_id');
        const redirectStatus = searchParams.get('redirect_status');

        if (!klarnaAuthId) {
            setState('error');
            setErrorMessage(language === Language.GERMAN
                ? 'Fehlende Autorisierungsinformationen.'
                : 'Missing authorization information.');
            return;
        }

        if (redirectStatus !== 'succeeded') {
            const cancelledMessage = language === Language.GERMAN
                ? 'Die Klarna-Zahlung wurde abgelehnt oder abgebrochen.'
                : 'The Klarna payment was declined or cancelled.';
            if (embedReturnPage) {
                cancelCheckoutMutation.mutateAsync({ klarnaAuthorizationId: klarnaAuthId })
                    .catch(() => undefined)
                    .finally(() => handOverToDrawer(embedReturnPage, '/review-booking', { errorMessage: cancelledMessage }));
                return;
            }
            cancelCheckoutMutation.mutate({ klarnaAuthorizationId: klarnaAuthId });
            toast.error(cancelledMessage);
            navigate('/review-booking', { replace: true });
            return;
        }

        confirmMutation.mutate(
            { klarnaAuthorizationId: klarnaAuthId },
            {
                onSuccess: (result) => {
                    if (result.success) {
                        setState('success');

                        if (selectedStore && cartItems.length > 0) {
                            sendEvent(GTMEvent.BOOKING_FUNNEL_BOOKED, {
                                is_first_booking: result.isFirstBooking === true,
                                transaction_id: result.appointmentId,
                                appointment: {
                                    datetime: selectedTimeslot?.getDisplayDateTime(),
                                    notify_about_earlier_possible_appointment: false,
                                },
                                total_net: toFixedNumber(
                                    cartItems
                                        .map((item) => getCartItemGtmTotalPrice(item) / 1.19)
                                        .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
                                ),
                                total_gross: toFixedNumber(
                                    cartItems
                                        .map((item) => getCartItemGtmTotalPrice(item))
                                        .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
                                ),
                                coupon: discountCode,
                                cart_items: toGtmCartItems(cartItems),
                                branch: {
                                    id: selectedStore?.id,
                                    name: selectedStore?.name,
                                },
                                user: {
                                    id: null,
                                    has_account: false,
                                    email: email?.toLowerCase(),
                                    phone: phone,
                                },
                                payment_method: 'klarna',
                            });
                        }

                        setTimeout(() => {
                            if (embedReturnPage) {
                                resetBookingFlow();
                                handOverToDrawer(embedReturnPage, '/confirmation', { selectedStore });
                                return;
                            }
                            navigate("/confirmation", { state: { selectedStore } });
                        }, 2000);
                    } else {
                        setState('error');
                        setErrorMessage(result.error || (language === Language.GERMAN
                            ? 'Buchung fehlgeschlagen. Bitte versuchen Sie es erneut.'
                            : 'Booking failed. Please try again.'));
                    }
                },
                onError: (err) => {
                    setState('error');
                    setErrorMessage(err.message || (language === Language.GERMAN
                        ? 'Ein Fehler ist aufgetreten.'
                        : 'An error occurred.'));
                },
            }
        );
    }, []);

    const handleBack = () => {
        const target = selectedStore ? "/review-booking" : "/";
        if (embedReturnPageRef.current) {
            handOverToDrawer(embedReturnPageRef.current, target, null);
            return;
        }
        navigate(target, { replace: true });
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="max-w-md w-full text-center flex flex-col items-center gap-6">
                {state === 'processing' && (
                    <>
                        <Loader2 className="w-12 h-12 text-burgundy animate-spin" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {language === Language.GERMAN
                                    ? 'Ihre Buchung wird abgeschlossen...'
                                    : 'Completing your booking...'}
                            </h2>
                            <p className="text-sm text-burgundy/60 font-light">
                                {language === Language.GERMAN
                                    ? 'Bitte warten Sie, während wir Ihre Klarna-Zahlung bestätigen und Ihren Termin buchen.'
                                    : 'Please wait while we confirm your Klarna payment and book your appointment.'}
                            </p>
                        </div>
                    </>
                )}

                {state === 'success' && (
                    <>
                        <CheckCircle className="w-12 h-12 text-green-600" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {language === Language.GERMAN
                                    ? 'Buchung erfolgreich!'
                                    : 'Booking successful!'}
                            </h2>
                            <p className="text-sm text-burgundy/60 font-light">
                                {language === Language.GERMAN
                                    ? 'Sie werden in Kürze weitergeleitet...'
                                    : 'Redirecting you shortly...'}
                            </p>
                        </div>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <XCircle className="w-12 h-12 text-red-500" />
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-medium text-burgundy">
                                {language === Language.GERMAN
                                    ? 'Etwas ist schiefgelaufen'
                                    : 'Something went wrong'}
                            </h2>
                            <p className="text-sm text-red-600 font-light">
                                {errorMessage}
                            </p>
                        </div>
                        <button
                            onClick={handleBack}
                            className="mt-4 px-6 py-3 bg-burgundy text-white rounded-lg text-sm font-medium hover:bg-burgundy/90 transition-colors"
                        >
                            {selectedStore
                                ? (language === Language.GERMAN ? 'Zurück zur Buchungsübersicht' : 'Back to booking overview')
                                : (language === Language.GERMAN ? 'Zurück zur Startseite' : 'Back to home')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default KlarnaReturn;
